import { CARD_PADDED } from '@/components/common/surfaces';
import type { UploadQueueEntry } from '@/hooks/useUploadQueue';
import { CheckCircle, CircleNotch, UploadSimple, WarningCircle } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadQueuePanelProps {
  uploads: UploadQueueEntry[];
  onCancel?: (id: string) => void;
}

/**
 * The queue of uploads still running, pinned to the bottom-right corner.
 *
 * It takes the page's own card rather than a floating panel style of its own, because it outlives
 * the page it was started from: the same box follows the user into a project detail, and it should
 * look like it belongs to whatever is behind it.
 */
export const UploadQueuePanel: FC<UploadQueuePanelProps> = ({ uploads }) => {
  const { t } = useTranslation();

  if (uploads.length === 0) {
    return null;
  }

  return (
    <section
      className={`${CARD_PADDED} fixed bottom-20 right-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-4 shadow-lg`}
      aria-label={t('projects.uploadQueueLabel', { count: uploads.length })}
    >
      <h2 className="m-0 text-sm font-semibold text-gray-900">
        {t('projects.uploadingDocuments')} ({uploads.length})
      </h2>
      <ul className="m-0 flex max-h-64 list-none flex-col gap-4 overflow-y-auto p-0">
        {uploads.map((entry) => {
          const running = entry.status === 'uploading' || entry.status === 'processing';

          return (
            <li key={entry.id} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 shrink-0">
                {running ? (
                  <CircleNotch size={16} className="animate-spin text-gray-400" aria-hidden="true" />
                ) : entry.status === 'done' ? (
                  <CheckCircle size={16} className="text-emerald-600" aria-hidden="true" />
                ) : (
                  <WarningCircle size={16} className="text-red-500" aria-hidden="true" />
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex items-center gap-2">
                  <UploadSimple size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="truncate font-medium text-gray-900" title={entry.title}>
                    {entry.title}
                  </span>
                </span>
                <span className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500">
                  {entry.fileNames.map((name) => (
                    <span key={name} className="max-w-[180px] truncate" title={name}>
                      {name}
                    </span>
                  ))}
                </span>
                {running && (
                  <span className="mt-1 block h-1 overflow-hidden rounded-full bg-gray-100">
                    <span className="indeterminate-progress-bar block h-full rounded-full bg-primary" />
                  </span>
                )}
                {entry.status === 'error' && entry.error && (
                  <span className="text-xs text-red-600" title={entry.error}>
                    {entry.error}
                  </span>
                )}
              </div>
              {/* TODO: Cancel upload button — hidden until backend supports preemptive task cancellation.
                   Backend cooperative cancel (Redis flag) does not interrupt _upload_3d_model mid-execution.
                   See future-extensions.md for the planned implementation. When it lands, this is an
                   `ActionButton variant="danger-ghost" iconOnly` carrying a Phosphor `X`.
              {onCancel && entry.cancel && running && (
                <ActionButton
                  variant="danger-ghost"
                  iconOnly={true}
                  icon={<X size={16} aria-hidden="true" />}
                  onClick={() => onCancel(entry.id)}
                  title={t('projects.cancelUpload')}
                  aria-label={t('projects.cancelUpload')}
                />
              )} */}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
